import { css } from '@emotion/react';
import { Box, Typography } from '@mui/material';
import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { PaymentOptionBanner } from 'src/components/payment-option-banner';
import { Section } from 'src/components/section';
import { CustomTooltip } from 'src/components/tooltip';
import { formatAmount } from 'src/utils/format-amount';

import { totalPaymentData } from '../booking-summary/constants';

import { styles } from './styles';
import { PaymentPlanSkeleton } from './payment-plan-skeleton';

import type { PaymentPlanViewProps } from './types';

export const getRowStyles = (isDiscount = false, withBorder = false) => {
    if (withBorder) {
        return css(styles.tableRow, styles.tableRowWithBorder);
    }

    if (isDiscount) {
        return css(styles.tableRow, styles.tableRowDiscount);
    }

    return styles.tableRow;
};

export const PaymentPlanView: FC<PaymentPlanViewProps> = ({
    showPaymentOptions = true,
    restStyles,
    withHeaderStipe = true,
    isEditBookingDetailsDialog = false,
    currency,
    createdPaymentPlan,
    loading = false,
    showSantanderCards = false,
    id,
}) => {
    const { t } = useTranslation();

    if (loading || !createdPaymentPlan) return <PaymentPlanSkeleton />;

    const isGapWeekEligible = createdPaymentPlan.fillerWeekEligible;

    return (
        <Section
            header={t('payment_plan_title')}
            withHeaderStipe={withHeaderStipe}
            {...(id && { id })}
        >
            <Box css={css(styles.wrapper, restStyles)}>
                <Box
                    css={[
                        styles.paymentPlanLineItems,
                        isEditBookingDetailsDialog && styles.noBorder,
                    ]}
                >
                    <Box css={css(styles.tableRow, styles.tableRowWithBorder)}>
                        <Box css={styles.firstCell}>
                            <Typography variant="h4" css={styles.paymentPlanColumnTitle}>
                                {t('payment_plan_payment_th')}
                            </Typography>
                        </Box>
                        <Box css={styles.secondCell}>
                            <Typography variant="h4" css={styles.paymentPlanColumnTitle}>
                                {t('payment_plan_due_date_th')}
                            </Typography>
                        </Box>
                        <Box css={styles.thirdCell}>
                            <Typography variant="h4" css={styles.paymentPlanColumnTitle}>
                                {t('payment_plan_amount_th')}
                            </Typography>
                        </Box>
                    </Box>

                    <Box css={css(styles.tableRowWithBorder)}>
                        {createdPaymentPlan.paymentOptions.map((plan) => (
                            <Box key={plan.name} css={getRowStyles(false, false)}>
                                <Box css={styles.firstCell}>
                                    <Typography variant="body2" css={styles.tableContent}>
                                        {plan.name}
                                    </Typography>
                                </Box>
                                <Box css={styles.secondCell}>
                                    {plan.dueDate ? (
                                        <Typography variant="body2" css={styles.tableContent}>
                                            {plan.dueDate}
                                        </Typography>
                                    ) : null}
                                </Box>
                                <Box css={styles.thirdCell}>
                                    <Typography variant="body2" css={styles.tableContent}>
                                        {formatAmount(plan.price.toFixed(2), currency)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}

                        {isGapWeekEligible && (
                            <Box css={styles.gapWeekRow}>
                                <Typography variant="body4" css={styles.gapWeekText}>
                                    <Trans
                                        i18nKey="payment_plan_gap_week"
                                        components={{
                                            span: <span css={styles.gapWeekSpan} />,
                                        }}
                                    />
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Box css={css(styles.tableRowWithBorder, styles.tableRowWithDarkBorder)}>
                        {createdPaymentPlan.promotion && (
                            <Box>
                                <Box css={getRowStyles(false, false)}>
                                    <Box css={styles.firstCell}>
                                        <Typography variant="body2">
                                            {t('payment_plan_subtotal')}
                                        </Typography>
                                    </Box>
                                    <Box css={styles.thirdCell}>
                                        <Typography variant="body2">
                                            {formatAmount(
                                                createdPaymentPlan.subtotal.toFixed(2),
                                                currency
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box css={[getRowStyles(false, false), styles.discount]}>
                                    <Box css={styles.firstCell}>
                                        <Typography variant="body2" css={styles.discountLine}>
                                            {t('payment_plan_discount', {
                                                count: createdPaymentPlan.promotion.number_of_weeks,
                                            })}
                                        </Typography>
                                    </Box>
                                    <Box css={styles.thirdCell}>
                                        <Typography variant="body2" css={styles.discountLine}>
                                            -{' '}
                                            {formatAmount(
                                                createdPaymentPlan.promotion.amount.toFixed(2),
                                                currency
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                        <Box css={getRowStyles(false, false)}>
                            <Box css={styles.firstCell}>
                                <Typography variant="body2">
                                    {t('payment_plan_total_payable')}
                                </Typography>
                            </Box>
                            <Box css={styles.thirdCell}>
                                <Typography variant="body2">
                                    {formatAmount(
                                        createdPaymentPlan.totalRent.toFixed(2),
                                        currency
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        css={css(
                            styles.tableRow,
                            styles.tableRowWithBorder,
                            styles.tableRowWithDarkBorder
                        )}
                    >
                        <Box css={[styles.firstCell, styles.twoRows]}>
                            <Typography variant="h3" css={styles.totalPay}>
                                {t(totalPaymentData.paymentLocale)}
                            </Typography>
                            <CustomTooltip
                                title={
                                    <Typography variant="body4">
                                        {t('payment_plan_total_payable_info')}
                                    </Typography>
                                }
                            />
                        </Box>
                        <Box css={styles.thirdCell}>
                            <Typography
                                variant="h3"
                                css={css(styles.totalPay, styles.totalPayAmount)}
                            >
                                {formatAmount(
                                    createdPaymentPlan.paymentOptions[0].price.toFixed(2),
                                    currency
                                )}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {!isEditBookingDetailsDialog && (
                    <PaymentOptionBanner
                        showPaymentOptions={showPaymentOptions}
                        showSantanderCards={showSantanderCards}
                    />
                )}
            </Box>
        </Section>
    );
};
